import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { employeeApi, attendanceApi, leaveApi, payrollApi, dashboardApi, compoffApi } from '@/lib/api';
import { formatCurrency } from '@/lib/payrollCalc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Users, UserCheck, CalendarOff, Receipt, Check, X, UserPlus, UserMinus } from 'lucide-react';
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ReactECharts from 'echarts-for-react';
import { toast } from 'sonner';

const HEADCOUNT_VIEWS = [
  { key: 'companyCounts', label: 'Company' },
  { key: 'departmentCounts', label: 'Department' },
  { key: 'projectCounts', label: 'Project' },
];
const HEADCOUNT_VIEWPORT_HEIGHT = 380;
const HEADCOUNT_ROW_HEIGHT = 44;
const HEADCOUNT_X_AXIS_HEIGHT = 40;
const HEADCOUNT_GRID = { left: 148, right: 56 };

const normalizeHeadcountRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  const mapped = rows.map((row) => ({
    name: row?.name || 'Unknown',
    count: Number(row?.count) || 0,
    percentage: Number(row?.percentage) || 0,
  }));
  const total = mapped.reduce((sum, row) => sum + row.count, 0);
  return mapped.map((row) => ({
    ...row,
    percentage: row.percentage || (total ? Math.round((row.count / total) * 1000) / 10 : 0),
  }));
};

const isCompOffType = (type) => type === 'CompOff' || type === 'CO';

const isCompOffRecord = (leave) =>
  leave?.isCompOff === true ||
  isCompOffType(leave?.type) ||
  String(leave?.leaveId || leave?.compoffId || '').startsWith('CO');

const resolveReviewLeaveType = (leave) =>
  (isCompOffRecord(leave) ? 'CompOff' : leave?.type) || 'EL';

const normalizeCompOffAsLeave = (compoff) => {
  if (!compoff) return null;
  const id = compoff.compoffId || compoff.leaveId || compoff.id || compoff._id;
  return {
    ...compoff,
    leaveId: id,
    compoffId: id,
    type: 'CompOff',
    isCompOff: true,
  };
};

const normalizeEmployeeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.employees)) return value.employees;
  return [];
};

const normalizeJoinedExitedResponse = (response) => {
  if (!response) return { joined: [], exited: [] };

  const payload =
    response?.joined || response?.exited
      ? response
      : response?.data?.joined || response?.data?.exited
        ? response.data
        : response?.items?.joined || response?.items?.exited
          ? response.items
          : response?.data ?? response;

  return {
    joined: normalizeEmployeeList(payload?.joined ?? payload?.joinedEmployees),
    exited: normalizeEmployeeList(payload?.exited ?? payload?.exitedEmployees),
  };
};

const getEmployeeDisplayName = (emp) =>
  emp?.name ||
  emp?.employeeName ||
  `${emp?.firstName || ''} ${emp?.lastName || ''}`.trim() ||
  '-';

const getEmployeeId = (emp) => emp?.empId || emp?.employeeId || emp?.id || '';

const getEmployeeJoinDate = (emp) =>
  emp?.joiningDate || emp?.joinDate || emp?.dateOfJoining || emp?.doj || '';

const getEmployeeExitDate = (emp) =>
  emp?.inactiveDate || emp?.exitDate || emp?.relievingDate || emp?.lastWorkingDate || '';

const getJoinedExitedFromDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function DashboardPage() {
  const { user, hasAccess } = useAuth();
  const [chartMonth, setChartMonth] = useState(() => {
    const n = new Date();
    return String(n.getMonth() + 1).padStart(2, '0');
  });
  const [chartYear, setChartYear] = useState(() => String(new Date().getFullYear()));

  const [employees, setEmployees] = useState([]);
  const [chartAttendance, setChartAttendance] = useState([]);
  const [todayMonthAttendance, setTodayMonthAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payRuns, setPayRuns] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [joinedEmployees, setJoinedEmployees] = useState([]);
  const [exitedEmployees, setExitedEmployees] = useState([]);
  const [joinedExitedFromDate, setJoinedExitedFromDate] = useState(getJoinedExitedFromDate);
  const [movementLoading, setMovementLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const isFirstLoadRef = useRef(true);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmReviewOpen, setConfirmReviewOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewType, setReviewType] = useState('EL');
  const [pendingReviewAction, setPendingReviewAction] = useState('');
  const [headcountView, setHeadcountView] = useState('companyCounts');
  const [movementView, setMovementView] = useState('joined');

  const isEmployee = user?.role === 'Employee';
  const isManager = user?.role === 'Manager';
  const showReviewActions = !isManager;

  const isOwnLeave = (leave) => {
    const leaveEmployeeId = leave?.employeeId;
    const userEmployeeId = user?.employeeId;
    if (!leaveEmployeeId || !userEmployeeId) return false;
    return String(leaveEmployeeId) === String(userEmployeeId);
  };

  const canShowReviewButton = (leave) => {
    if (isManager) return false;
    if (hasAccess('HR')) return true;
    if (isEmployee) return !isOwnLeave(leave);
    return false;
  };

  const getEmployeeBalance = (employeeId) => {
    const emp = employees.find((e) => (e?.empId || e?.id) === employeeId);
    return emp?.leaveBalance ?? { EL: 0, CL: 0, ML: 0, LOP: 0 };
  };

  const normalizeArrayResponse = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    return [];
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (isFirstLoadRef.current) setLoading(true);
      try {
        const [statsData, emps, atts, lvs, cos, prs] = await Promise.all([
          dashboardApi.getStats().catch(() => null),
          employeeApi.getAll().catch(() => []),
          attendanceApi.get({ month: chartMonth, year: chartYear }).catch(() => []),
          leaveApi.getAll().catch(() => []),
          compoffApi.getAll().catch(() => []),
          payrollApi.getAll().catch(() => []),
        ]);
        if (cancelled) return;
        const rows = normalizeArrayResponse(atts);
        setDashboardStats(statsData);
        setEmployees(normalizeArrayResponse(emps));
        setChartAttendance(rows);
        const n = new Date();
        const y = parseInt(chartYear, 10);
        const m = parseInt(chartMonth, 10);
        if (y === n.getFullYear() && m === n.getMonth() + 1) {
          setTodayMonthAttendance(rows);
        }
        const leaveItems = normalizeArrayResponse(lvs);
        const compoffItems = normalizeArrayResponse(cos)
          .map(normalizeCompOffAsLeave)
          .filter(Boolean);
        const mergedLeaves = [...leaveItems, ...compoffItems].sort((a, b) => {
          const aTime = new Date(a?.appliedOn || a?.createdAt || 0).getTime();
          const bTime = new Date(b?.appliedOn || b?.createdAt || 0).getTime();
          return bTime - aTime;
        });
        setLeaves(mergedLeaves);
        setPayRuns(normalizeArrayResponse(prs));
      } finally {
        if (!cancelled && isFirstLoadRef.current) {
          setLoading(false);
          isFirstLoadRef.current = false;
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [chartMonth, chartYear]);

  useEffect(() => {
    if (!joinedExitedFromDate) return undefined;
    let cancelled = false;
    const run = async () => {
      setMovementLoading(true);
      try {
        const movementData = await employeeApi
          .getJoinedExited({ fromDate: joinedExitedFromDate })
          .catch(() => null);
        if (cancelled) return;
        const { joined, exited } = normalizeJoinedExitedResponse(movementData);
        setJoinedEmployees(joined);
        setExitedEmployees(exited);
      } finally {
        if (!cancelled) setMovementLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [joinedExitedFromDate]);

  useEffect(() => {
    let cancelled = false;
    const n = new Date();
    const tm = String(n.getMonth() + 1).padStart(2, '0');
    const ty = String(n.getFullYear());
    if (tm === chartMonth && ty === chartYear) return;
    (async () => {
      const atts = await attendanceApi.get({ month: tm, year: ty }).catch(() => []);
      if (cancelled) return;
      setTodayMonthAttendance(normalizeArrayResponse(atts));
    })();
    return () => {
      cancelled = true;
    };
  }, [chartMonth, chartYear]);

  const stats = useMemo(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const chartY = parseInt(chartYear, 10);
    const chartM = parseInt(chartMonth, 10);
    const activeEmployees = employees.filter((e) => e.status === 'Active');
    const todayAtt = todayMonthAttendance.filter((a) => a.date === today);
    const presentToday = todayAtt.filter((a) => a.status === 'Present').length;
    const lateToday = todayAtt.filter((a) => a.isLate).length;
    const pendingLeaves = leaves.filter((l) => l.status === 'Pending').length;
    const approvedLeavesThisMonth = leaves.filter(
      (l) => l.status === 'Approved' && l.startDate?.startsWith(thisMonth)
    ).length;

    const monthData = [];
    const daysInMonth = new Date(chartY, chartM, 0).getDate();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const isChartCurrentMonth = chartY === now.getFullYear() && chartM === now.getMonth() + 1;
    for (let d = 1; d <= daysInMonth; d += 1) {
      const cellDate = new Date(chartY, chartM - 1, d);
      if (isChartCurrentMonth && cellDate > todayStart) break;
      const ds = `${chartYear}-${chartMonth}-${String(d).padStart(2, '0')}`;
      const dayAtt = chartAttendance.filter((a) => a.date === ds);
      monthData.push({
        day: String(d),
        present: dayAtt.filter((a) => a.status === 'Present').length,
        absent: dayAtt.filter((a) => a.status === 'Absent').length,
        late: dayAtt.filter((a) => a.isLate).length,
      });
    }

    return {
      totalEmployees: activeEmployees.length,
      presentToday,
      lateToday,
      pendingLeaves,
      approvedLeavesThisMonth,
      lastPayRun: payRuns[0],
      monthData,
    };
  }, [employees, chartAttendance, todayMonthAttendance, leaves, payRuns, chartMonth, chartYear]);

  const resolvedStats = useMemo(() => {
    const lastPayrollValue = dashboardStats?.lastPayroll ?? stats.lastPayRun ?? null;
    const lastPayrollAmount = typeof lastPayrollValue === 'number'
      ? lastPayrollValue
      : (lastPayrollValue?.totalNet ?? null);
    const lastPayrollEmployeeCount = lastPayrollValue?.employeeCount ?? null;

    return {
      totalEmployees: dashboardStats?.totalEmployees ?? stats.totalEmployees,
      lateToday: dashboardStats?.lateToday ?? stats.lateToday,
      pendingLeaves: dashboardStats?.pendingLeaves ?? stats.pendingLeaves,
      totalProjects: dashboardStats?.totalProjects ?? 0,
      approvedLeavesThisMonth: dashboardStats?.approvedLeavesThisMonth ?? stats.approvedLeavesThisMonth,
      payRunsProcessed: dashboardStats?.payRunsProcessed ?? payRuns.length,
      lastPayrollAmount: dashboardStats?.lastPayrollAmount ?? lastPayrollAmount,
      lastPayrollEmployeeCount: dashboardStats?.lastPayrollEmployeeCount ?? lastPayrollEmployeeCount,
    };
  }, [dashboardStats, stats, payRuns.length]);

  const headcountBreakdown = useMemo(() => {
    const breakdown = dashboardStats?.headcountBreakdown;
    return {
      companyCounts: normalizeHeadcountRows(breakdown?.companyCounts),
      departmentCounts: normalizeHeadcountRows(breakdown?.departmentCounts),
      projectCounts: normalizeHeadcountRows(breakdown?.projectCounts),
    };
  }, [dashboardStats]);

  const headcountRows = headcountBreakdown?.[headcountView] ?? [];

  const headcountChart = useMemo(() => {
    const names = headcountRows.map((row) => row.name);
    const counts = headcountRows.map((row) => row.count);
    const xMax = Math.max(1, Math.ceil(Math.max(0, ...counts) * 1.2));
    const tooltip = {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const point = Array.isArray(params) ? params[0] : params;
        const row = headcountRows[point?.dataIndex] ?? {};
        return `${row.name || point?.name || ''}<br/>Headcount: ${row.count ?? 0} (${row.percentage ?? 0}%)`;
      },
    };
    const barOption = {
      tooltip,
      grid: { top: 8, bottom: 4, containLabel: false, ...HEADCOUNT_GRID },
      xAxis: {
        type: 'value',
        min: 0,
        max: xMax,
        minInterval: 1,
        axisLabel: { show: false },
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: names,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          fontSize: 11,
          color: '#334155',
          width: 140,
          overflow: 'truncate',
        },
      },
      series: [
        {
          name: 'Headcount',
          type: 'bar',
          data: counts,
          barMaxWidth: 28,
          itemStyle: { color: '#0047AB', borderRadius: [0, 4, 4, 0] },
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            color: '#64748b',
            formatter: (params) => {
              const row = headcountRows[params?.dataIndex];
              if (!row) return '';
              return `${row.count} (${row.percentage}%)`;
            },
          },
        },
      ],
    };
    const axisOption = {
      grid: { top: 0, bottom: 22, containLabel: false, ...HEADCOUNT_GRID },
      xAxis: {
        type: 'value',
        min: 0,
        max: xMax,
        minInterval: 1,
        axisLabel: { fontSize: 11, color: '#64748b' },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      yAxis: { type: 'category', data: [''], show: false },
      series: [{ type: 'bar', data: [0], silent: true, itemStyle: { opacity: 0 } }],
    };
    const scrollHeight = HEADCOUNT_VIEWPORT_HEIGHT - HEADCOUNT_X_AXIS_HEIGHT;
    return {
      barOption,
      axisOption,
      innerHeight: Math.max(scrollHeight, names.length * HEADCOUNT_ROW_HEIGHT),
      scrollHeight,
    };
  }, [headcountRows]);

  const pendingLeaves = useMemo(() => {
    return leaves.filter((l) => l.status === 'Pending');
  }, [leaves]);

  const pendingColSpan = 6 + (showReviewActions ? 1 : 0);

  const movementEmployees = movementView === 'joined' ? joinedEmployees : exitedEmployees;
  const movementTotal = joinedEmployees.length + exitedEmployees.length;

  const handleOpenReview = (leave) => {
    if (!leave) return;
    setSelectedLeave(leave);
    setReviewType(isCompOffRecord(leave) ? 'CompOff' : (leave?.type || 'EL'));
    setReviewComment('');
    setReviewOpen(true);
  };

  const handleReview = async (action) => {
    if (!selectedLeave) return;
    if (action === 'Rejected' && !reviewComment?.trim()) {
      toast.error('Comment is required when rejecting a leave request');
      return;
    }
    try {
      const leaveId = selectedLeave.leaveId || selectedLeave.compoffId || selectedLeave.id;
      const reviewingAsCompOff = isCompOffType(reviewType);
      let updated;
      if (isCompOffRecord(selectedLeave) && reviewingAsCompOff) {
        updated = normalizeCompOffAsLeave(
          await compoffApi.review(leaveId, {
            status: action,
            reviewComment,
          })
        );
      } else {
        updated = await leaveApi.review(leaveId, {
          status: action,
          reviewComment,
          type: reviewType,
        });
        if (reviewingAsCompOff) updated = normalizeCompOffAsLeave(updated);
      }
      setLeaves((prev) =>
        prev.map((l) => ((l.leaveId || l.id) === leaveId ? { ...l, ...updated } : l))
      );
      setDashboardStats((prev) =>
        prev
          ? { ...prev, pendingLeaves: Math.max(0, (prev.pendingLeaves ?? 1) - 1) }
          : prev
      );
      toast.success(`${isCompOffRecord(selectedLeave) ? 'CompOff' : 'Leave'} ${action.toLowerCase()}`);
      setReviewOpen(false);
      setSelectedLeave(null);
      setReviewComment('');
      setReviewType('EL');
    } catch (err) {
      toast.error(err.message || 'Failed to review leave');
    }
  };

  const handleOpenReviewConfirm = (action) => {
    if (!selectedLeave) return;
    if (action === 'Rejected' && !reviewComment?.trim()) {
      toast.error('Comment is required when rejecting a leave request');
      return;
    }
    setPendingReviewAction(action);
    setConfirmReviewOpen(true);
  };

  const handleConfirmReview = async () => {
    if (!pendingReviewAction) return;
    await handleReview(pendingReviewAction);
    setConfirmReviewOpen(false);
    setPendingReviewAction('');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" /></div>;

  const metricCards = [
    { title: 'Total Employees', value: resolvedStats.totalEmployees, icon: Users, color: '#0047AB', subtext: 'Active' },
    { title: 'Pending Leaves', value: resolvedStats.pendingLeaves, icon: CalendarOff, color: '#f59e0b', subtext: 'Awaiting approval' },
    { title: 'Total Projects', value: resolvedStats.totalProjects, icon: UserCheck, color: '#10b981', subtext: 'Active projects' },
    {
      title: 'Last Payroll',
      value: resolvedStats.lastPayrollAmount ? formatCurrency(resolvedStats.lastPayrollAmount) : 'N/A',
      icon: Receipt,
      color: '#6366f1',
      subtext: resolvedStats.lastPayrollEmployeeCount ? `${resolvedStats.lastPayrollEmployeeCount} employees` : '',
    },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-3 animate-fadeIn">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-[Manrope]">Welcome back, {user?.name?.split(' ')[0]}</h1>
        <p className="text-muted-foreground mt-1">Here's what's happening with your team today.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((card, i) => (
          <Card key={card.title} className="border border-border shadow-sm hover:shadow-md transition-shadow animate-fadeIn" style={{ animationDelay: `${i * 0.05}s` }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest font-medium text-muted-foreground">{card.title}</p>
                  <p className="text-2xl font-bold tracking-tight mt-2 font-[Manrope]" data-testid={`metric-${card.title.toLowerCase().replace(/\s/g, '-')}`}>{card.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{card.subtext}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: card.color + '10' }}><card.icon className="w-5 h-5" style={{ color: card.color }} /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Attendance overview graph
        <Card className="lg:col-span-8 border border-border shadow-sm">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:space-y-0 pb-2">
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold font-[Manrope] flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#0047AB]" />
                Monthly Attendance Overview
              </CardTitle>
              <p className="text-xs text-muted-foreground font-normal">
                Daily counts for{' '}
                {new Date(parseInt(chartYear, 10), parseInt(chartMonth, 10) - 1, 1).toLocaleString('default', {
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Select value={chartMonth} onValueChange={setChartMonth}>
                <SelectTrigger className="w-full min-w-0 sm:w-[140px]" data-testid="dashboard-chart-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>
                      {new Date(2026, i, 1).toLocaleString('default', { month: 'long' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={chartYear} onValueChange={setChartYear}>
                <SelectTrigger className="w-full min-w-0 sm:w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] sm:h-[320px]" data-testid="attendance-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.monthData}
                  margin={{ top: 28, right: 8, left: 4, bottom: 4 }}
                  barCategoryGap="18%"
                  maxBarSize={48}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={0} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={40} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(day) => `Day ${day}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" verticalAlign="top" height={28} />
                  <Bar dataKey="present" stackId="day" fill="#0047AB" name="Present" />
                  <Bar dataKey="absent" stackId="day" fill="#ef4444" name="Absent" />
                  <Bar dataKey="late" stackId="day" fill="#f59e0b" name="Late" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        */}
        <Card className="lg:col-span-12 border border-border shadow-sm">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0 pb-2">
            <div>
              <CardTitle className="text-base font-semibold font-[Manrope] flex items-center gap-2">
                <Users className="w-4 h-4 text-[#0047AB]" />
                Headcount Breakdown
              </CardTitle>
              <p className="text-xs text-muted-foreground font-normal mt-1">
                Employee count and share by company, department, or project
              </p>
            </div>
            <Tabs value={headcountView} onValueChange={setHeadcountView} className="w-full sm:w-auto">
              <TabsList className="grid w-full grid-cols-3 sm:w-auto sm:inline-flex">
                {HEADCOUNT_VIEWS.map((view) => (
                  <TabsTrigger key={view.key} value={view.key} className="text-xs sm:text-sm">
                    {view.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <div
              className="w-full flex flex-col"
              style={{ height: HEADCOUNT_VIEWPORT_HEIGHT }}
              data-testid="headcount-chart"
            >
              {headcountRows.length ? (
                <>
                  <div
                    className="min-h-0 overflow-y-auto overflow-x-hidden"
                    style={{ height: headcountChart.scrollHeight }}
                  >
                    <ReactECharts
                      option={headcountChart.barOption}
                      style={{ height: headcountChart.innerHeight, width: '100%' }}
                      opts={{ renderer: 'svg' }}
                      notMerge
                      onChartReady={(chart) => {
                        chart?.getZr?.()?.off?.('mousewheel');
                      }}
                    />
                  </div>
                  <div className="shrink-0" style={{ height: HEADCOUNT_X_AXIS_HEIGHT }}>
                    <ReactECharts
                      option={headcountChart.axisOption}
                      style={{ height: '100%', width: '100%' }}
                      opts={{ renderer: 'svg' }}
                      notMerge
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-16 sm:py-20">No headcount data</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <Card className="border border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-semibold font-[Manrope] flex items-center gap-2">
            <CalendarOff className="w-4 h-4 text-[#f59e0b]" />
            Pending Approvals
            {pendingLeaves.length > 0 && (
              <Badge className="ml-1 bg-yellow-100 text-yellow-700 border-yellow-200 text-xs">
                {pendingLeaves.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto" data-testid="pending-leaves">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">From</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">To</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Reason</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-center">Balance</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Applied On</TableHead>
                  {showReviewActions && (
                    <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingLeaves.map((leave) => {
                  const bal = leave?.leaveBalance ?? getEmployeeBalance(leave?.employeeId);
                  return (
                    <TableRow
                      key={leave.leaveId || leave.id}
                      data-testid={`dashboard-pending-leave-${leave.leaveId || leave.id}`}
                    >
                      <TableCell className="text-sm font-medium">
                        {leave?.employeeId ? (
                          <Link
                            to={`/employees/${leave.employeeId}`}
                            className="hover:text-[#0047AB] hover:underline"
                            data-testid={`dashboard-employee-link-${leave.employeeId}`}
                          >
                            {leave.employeeName}
                          </Link>
                        ) : (
                          leave.employeeName
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{leave.type}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{leave.startDate}</TableCell>
                      <TableCell className="font-mono text-xs">{leave.endDate}</TableCell>
                      <TableCell className="text-sm max-w-[160px]">
                        {leave?.reason ? (
                          <HoverCard openDelay={150} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <button
                                type="button"
                                className="max-w-full truncate cursor-help outline-none text-left"
                                aria-label="View full reason"
                              >
                                {leave.reason}
                              </button>
                            </HoverCardTrigger>
                            <HoverCardContent align="start" className="w-72 p-3 space-y-1.5">
                              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Reason</p>
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{leave.reason}</p>
                            </HoverCardContent>
                          </HoverCard>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-xs">
                          <span className="text-[#0047AB] font-mono">EL:{bal?.EL ?? 0}</span>
                          <span className="text-muted-foreground mx-1">|</span>
                          <span className="text-[#6366f1] font-mono">CL:{bal?.CL ?? 0}</span>
                          <span className="text-muted-foreground mx-1">|</span>
                          <span className="text-green-600 font-mono">ML:{bal?.ML ?? 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {leave?.appliedOn ? new Date(leave.appliedOn).toLocaleDateString() : '-'}
                      </TableCell>
                      {showReviewActions && (
                        <TableCell>
                          {canShowReviewButton(leave) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => handleOpenReview(leave)}
                              data-testid={`dashboard-review-leave-${leave.leaveId || leave.id}`}
                            >
                              Review
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {pendingLeaves.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={pendingColSpan} className="text-center py-8 text-muted-foreground">
                      <CalendarOff className="w-6 h-6 mx-auto mb-2 opacity-50" />
                      No pending leave requests
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border shadow-sm">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
          <div>
            <CardTitle className="text-base font-semibold font-[Manrope] flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#10b981]" />
              Joined & Exited
              {movementTotal > 0 && (
                <Badge className="ml-1 bg-slate-100 text-slate-700 border-slate-200 text-xs">
                  {movementTotal}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground font-normal mt-1">
              Employees who joined or exited from the selected date
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-2 min-w-0">
              <Label
                htmlFor="joined-exited-from-date"
                className="text-xs uppercase tracking-widest text-muted-foreground whitespace-nowrap"
              >
                From
              </Label>
              <Input
                id="joined-exited-from-date"
                type="date"
                value={joinedExitedFromDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => {
                  const next = e?.target?.value;
                  if (!next) return;
                  setJoinedExitedFromDate(next);
                }}
                className="w-full min-w-0 sm:w-[160px]"
                data-testid="dashboard-joined-exited-from-date"
              />
            </div>
            <Tabs value={movementView} onValueChange={setMovementView} className="w-full sm:w-auto">
              <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-flex">
                <TabsTrigger value="joined" className="text-xs sm:text-sm gap-1.5">
                  Joined
                  {joinedEmployees.length > 0 && (
                    <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] px-1.5 py-0">
                      {joinedEmployees.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="exited" className="text-xs sm:text-sm gap-1.5">
                  Exited
                  {exitedEmployees.length > 0 && (
                    <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] px-1.5 py-0">
                      {exitedEmployees.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto" data-testid="joined-exited">
            {movementLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-6 h-6 border-2 border-[#0047AB] border-t-transparent rounded-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Department</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Designation</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">
                      {movementView === 'joined' ? 'Joining Date' : 'Exit Date'}
                    </TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movementEmployees.map((emp, index) => {
                    const empId = getEmployeeId(emp);
                    const name = getEmployeeDisplayName(emp);
                    const dateValue =
                      movementView === 'joined' ? getEmployeeJoinDate(emp) : getEmployeeExitDate(emp);
                    return (
                      <TableRow
                        key={`${movementView}-${empId || name}-${index}`}
                        data-testid={`dashboard-${movementView}-${empId || index}`}
                      >
                        <TableCell className="text-sm font-medium">
                          {empId ? (
                            <Link
                              to={`/employees/${empId}`}
                              className="hover:text-[#0047AB] hover:underline"
                              data-testid={`dashboard-movement-link-${empId}`}
                            >
                              {name}
                            </Link>
                          ) : (
                            name
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{emp?.department || '-'}</TableCell>
                        <TableCell className="text-sm">{emp?.designation || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{dateValue || '-'}</TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              movementView === 'joined'
                                ? 'border-green-200 text-green-700 bg-green-50'
                                : 'border-red-200 text-red-700 bg-red-50'
                            }
                          >
                            {emp?.status || (movementView === 'joined' ? 'Joined' : 'Exited')}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {movementEmployees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        {movementView === 'joined' ? (
                          <UserPlus className="w-6 h-6 mx-auto mb-2 opacity-50" />
                        ) : (
                          <UserMinus className="w-6 h-6 mx-auto mb-2 opacity-50" />
                        )}
                        No {movementView} employees since {joinedExitedFromDate}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={reviewOpen}
        onOpenChange={(open) => {
          setReviewOpen(open);
          if (!open) {
            setSelectedLeave(null);
            setReviewComment('');
            setReviewType('EL');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Review Leave Request</DialogTitle>
            <DialogDescription>Approve or reject this leave application</DialogDescription>
          </DialogHeader>
          {selectedLeave && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Employee:</span>{' '}
                  <strong>{selectedLeave.employeeName}</strong>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Leave Type</Label>
                  <Select value={reviewType} onValueChange={setReviewType}>
                    <SelectTrigger className="mt-1" data-testid="dashboard-review-leave-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EL">Earned Leave (EL)</SelectItem>
                      <SelectItem value="CL">Casual Leave (CL)</SelectItem>
                      <SelectItem value="ML">Medical Leave (ML)</SelectItem>
                      <SelectItem value="LOP">Loss of Pay (LOP)</SelectItem>
                      <SelectItem value="ONDUTY">On Duty (ONDUTY)</SelectItem>
                      <SelectItem value="CompOff">Compensatory Off (CompOff)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <span className="text-muted-foreground">From:</span>{' '}
                  <strong className="font-mono">{selectedLeave.startDate}</strong>
                </div>
                <div>
                  <span className="text-muted-foreground">To:</span>{' '}
                  <strong className="font-mono">{selectedLeave.endDate}</strong>
                </div>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Reason:</span> {selectedLeave.reason}
              </div>
              {(() => {
                const bal = selectedLeave?.leaveBalance ?? getEmployeeBalance(selectedLeave?.employeeId);
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">EL Balance</p>
                      <p className={`text-lg font-bold font-mono ${reviewType === 'EL' ? 'text-[#0047AB]' : 'text-muted-foreground'}`}>
                        {bal?.EL || 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">CL Balance</p>
                      <p className={`text-lg font-bold font-mono ${reviewType === 'CL' ? 'text-[#6366f1]' : 'text-muted-foreground'}`}>
                        {bal?.CL || 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">ML Balance</p>
                      <p className={`text-lg font-bold font-mono ${reviewType === 'ML' ? 'text-[#10b981]' : 'text-muted-foreground'}`}>
                        {bal?.ML || 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">LOP Taken</p>
                      <p className={`text-lg font-bold font-mono ${reviewType === 'LOP' ? 'text-[#ef4444]' : 'text-muted-foreground'}`}>
                        {bal?.LOP || 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">CompOff</p>
                      <p className={`text-lg font-bold font-mono ${isCompOffType(reviewType) ? 'text-[#f59e0b]' : 'text-muted-foreground'}`}>
                        {selectedLeave?.leaveBalance?.CompOff ?? selectedLeave?.compOffBalance ?? 0}
                      </p>
                    </div>
                  </div>
                );
              })()}
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Comment</Label>
                <Textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Add a comment..."
                  data-testid="dashboard-review-comment"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => handleOpenReviewConfirm('Rejected')}
              data-testid="dashboard-reject-leave-btn"
            >
              <X className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button
              onClick={() => handleOpenReviewConfirm('Approved')}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="dashboard-approve-leave-btn"
            >
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmReviewOpen} onOpenChange={setConfirmReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {pendingReviewAction === 'Approved' ? 'Approve Leave Request?' : 'Reject Leave Request?'}
            </DialogTitle>
            <DialogDescription>
              {pendingReviewAction === 'Approved'
                ? 'Please confirm you want to approve this leave request.'
                : 'Please confirm you want to reject this leave request.'}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm space-y-1">
            <p>
              <span className="text-muted-foreground">Employee:</span>{' '}
              <strong>{selectedLeave?.employeeName || '-'}</strong>
            </p>
            <p>
              <span className="text-muted-foreground">Type:</span>{' '}
              <strong>{reviewType || '-'}</strong>
            </p>
            {reviewType !== resolveReviewLeaveType(selectedLeave) && (
              <p>
                <span className="text-muted-foreground">Originally applied as:</span>{' '}
                <strong>{selectedLeave?.type || '-'}</strong>
              </p>
            )}
            <p>
              <span className="text-muted-foreground">Dates:</span>{' '}
              <span className="font-mono">
                {selectedLeave?.startDate || '-'} to {selectedLeave?.endDate || '-'}
              </span>
            </p>
            {pendingReviewAction === 'Rejected' && (
              <p>
                <span className="text-muted-foreground">Comment:</span> {reviewComment?.trim() || '-'}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReviewOpen(false)}>
              Go Back
            </Button>
            <Button
              onClick={handleConfirmReview}
              className={
                pendingReviewAction === 'Approved'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }
              data-testid="dashboard-confirm-review-leave-btn"
            >
              {pendingReviewAction === 'Approved' ? 'Confirm Approve' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
